/**
 * Menneskene rundt én act (PERFORMANCE.md, fane 2 — People).
 *
 * ---------------------------------------------------------------------------------------------
 * TWO GROUPS IN ONE LIST, SEPARATED BY `party`, RATHER THAN TWO ENDPOINTS.
 *
 * The page draws «reisefølge» and «management» as two tables, and they are two tables because they
 * answer different questions — but they are ONE list on the wire. A page that fetched them
 * separately would make two requests to draw two halves of the same fact, and the third group on
 * that tab (the guest list) is genuinely a different endpoint because it is genuinely a different
 * table.
 *
 * ---------------------------------------------------------------------------------------------
 * WHAT A ROW CARRIES THAT THE CONTACT DOES NOT.
 *
 * The name, the phone and the email belong to the contact and are resolved for display. What is
 * about THIS person on THIS act is the role, the badge, what they cannot eat, and where they sleep
 * — and only the first three are stored here. `room` is resolved from the accommodation tab,
 * because «personens rom skal vises tilbake i People-listen» is a view of a fact that lives
 * somewhere else (principle 1: ett sted per opplysning).
 *
 * ---------------------------------------------------------------------------------------------
 * NOT PAGED. A touring party is nine people and a big one is thirty; `MAX_PERFORMANCE_PEOPLE` is
 * the ceiling that makes reading it whole a bounded cost, and a page of a crew list is a crew list
 * with people missing from it.
 */
import { z } from 'zod';

import { accreditationColourSchema } from './accreditation.js';
import { resolvedAccreditationLevelSchema } from './accreditations.js';

import { performancePartySchema } from '../enums.js';
import { rowViewerSchema } from './row-viewer.js';

/** How many people one act may carry. A touring party, not a database of everybody. */
export const MAX_PERFORMANCE_PEOPLE = 120;

export const MAX_PERFORMANCE_ROLE = 80;

/** Free text, and long enough for a real answer: «Nøtter, også spor — inkludert kryssforurensning». */
export const MAX_DIETARY_TEXT = 500;

/** Where somebody sleeps, resolved from the accommodation tab for display on the crew list. */
export const performanceRoomRefSchema = z
  .object({
    stayId: z.uuid(),
    stayName: z.string(),
    roomId: z.uuid(),
    roomNumber: z.string().nullable(),
  })
  .meta({ id: 'PerformanceRoomRef' });
export type PerformanceRoomRef = z.infer<typeof performanceRoomRefSchema>;

export const performancePersonDtoSchema = z
  .object({
    id: z.uuid(),
    performanceId: z.uuid(),
    contactId: z.uuid(),
    /** Resolved from the register, so a crew list does not have to join it. */
    displayName: z.string(),
    /**
     * 69 (253.20): står personen i organisasjonens kontaktregister? Personer som legges på ting, gjør
     * det ikke av seg selv; «Løft til kontakt» på profilen setter den. Valgfri for eldre testoppsett.
     */
    inRegister: z.boolean().optional(),
    /**
     * Funn 20: medlem av organisasjonen. Et medlem ser programposten gjennom rollen sin, så tilgangsvalget
     * («Bare oppført / Send lenke / Gi innlogging») gjelder bare den som er utenfor.
     */
    isMember: z.boolean().optional(),
    /** Mennesket bak kontakten — delingen peker på personen, ikke på kontaktraden. */
    personId: z.uuid(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    party: performancePartySchema,
    role: z.string().nullable(),
    accreditationLevelId: z.uuid().nullable(),
    /** The badge's name, or null for «ikke satt» — which the page draws as a gap, not a blank. */
    accreditationLevelName: z.string().nullable(),
    /** The wristband's own colour, so this page and the accreditation list agree. */
    accreditationLevelColour: accreditationColourSchema.nullable(),
    /**
     * NIVÅET PÅ ARMBÅNDET (215.21), og det lista viser. Feltene over er reisefølgets eget valg og det
     * det skriver; dette er hva akkrediteringen faktisk gir, og hvor det kom fra.
     */
    resolvedLevel: resolvedAccreditationLevelSchema,
    sortOrder: z.number().int(),
    /*
     * Health data. On the DTO because the crew list shows it as a note under the name — the person
     * ordering the food is reading that list — and reachable exactly when the booking is.
     */
    allergy: z.string().nullable(),
    diet: z.string().nullable(),
    dietaryNotes: z.string().nullable(),
    /*
     * KORTET OM PERSONEN (grep A1, 0915): «Allergier og kosthold» som tekst (A4, 1026), slik
     * `app.person_cards` gir den — personens egen når hen har konto, ellers husets. De tre over er
     * radens gamle kopier. Alle i huset ser kostholdet (1077).
     */
    food: z.string().nullable().optional(),
    /** Kostholdet er lest fra kortet: alltid for husets egne folk, aldri for en gjest (242.1). */
    healthVisible: z.boolean().optional(),
    /** Resolved from the accommodation tab. Null until somebody is given a room. */
    room: performanceRoomRefSchema.nullable(),
    /** Hva leseren kan gjøre med raden (D1), regnet av `can()` på serveren. */
    viewer: rowViewerSchema.optional(),
  })
  .meta({ id: 'PerformancePersonDto' });
export type PerformancePersonDto = z.infer<typeof performancePersonDtoSchema>;

export const performancePeopleResponseSchema = z
  .object({ items: z.array(performancePersonDtoSchema) })
  .meta({ id: 'PerformancePeopleResponse' });
export type PerformancePeopleResponse = z.infer<typeof performancePeopleResponseSchema>;

/**
 * Kvitteringen på «hent stamdata fra byrået» (vedtak 96.1, formen 101.3).
 *
 * ---------------------------------------------------------------------------------------------
 * FIRE TALL, OG DE TO SISTE ER DE VIKTIGSTE. Atle om varselet: «ikke en stille overskriving». Den
 * strengeste formen av det er å ikke overskrive i det hele tatt — så en person som alt står i
 * reisefølget blir stående, en lenke som alt er ført blir ikke ført igjen, og kvitteringen sier
 * hvor mange av hver. `skipped` er altså ikke en feil: det er svaret på «hva rørte den ikke».
 *
 * Fire nuller betyr «ingenting å hente», og det er ett av tre: ingen akseptert bud på
 * programposten, ingen stamdata hos byrået, eller en kobling som er sagt opp. Svaret skiller ikke
 * mellom dem — se `app.agency_roster_for_performance` for hvorfor.
 */
export const importAgencyRosterResponseSchema = z
  .object({
    people: z.number().int().nonnegative(),
    /**
     * De som alt sto der og fikk et hull fylt — allergi eller kosthold (QA runde 9, P1 #4).
     *
     * Et eget tall og ikke en del av `people`, fordi det er et annet svar til den som trykket:
     * ingen ny rad kom til, men noe kom likevel inn på en rad som fantes.
     */
    peopleUpdated: z.number().int().nonnegative(),
    peopleSkipped: z.number().int().nonnegative(),
    /**
     * Folk i byråets stall som IKKE ble lagt inn fordi programposten har en delt advance, der
     * byrået selv velger hvem motparten ser (QA6 K3 H1). Ikke en feil — kvitteringen sier hvorfor.
     */
    peopleHeldBack: z.number().int().nonnegative().default(0),
    deliveries: z.number().int().nonnegative(),
    deliveriesSkipped: z.number().int().nonnegative(),
    /**
     * DE BYRÅET HAR TATT UT SIDEN SIST (trinn 3, Atles avgjørelse 3).
     *
     * Et menneske som ikke lenger står i byråets roster SLETTES IKKE — festivalen kan ha gitt dem
     * akkreditering og satt dem på skift — men deltakelsen merkes «Tatt ut av byrået», og noen
     * rydder når de har sett den.
     *
     * Tallet står her fordi kvitteringen er stedet man SER at noe skjedde. Filhodet i
     * `AgencyRosterImportService` sier det om hele knappen: den er en knapp og ikke en automatikk
     * nettopp fordi den som trykker skal se hva den gjorde. Et uttak som bare dukket opp i lista
     * ville vært den stille endringen Atle ba oss unngå.
     */
    withdrawn: z.number().int().nonnegative(),
  })
  .meta({ id: 'ImportAgencyRosterResponse' });
export type ImportAgencyRosterResponse = z.infer<typeof importAgencyRosterResponseSchema>;

export const listPerformancePeopleQuerySchema = z
  .object({
    performanceId: z.uuid(),
    /** Absent means both groups, which is what the page asks for. */
    party: performancePartySchema.optional(),
  })
  .meta({ id: 'ListPerformancePeopleQuery' });
export type ListPerformancePeopleQuery = z.infer<typeof listPerformancePeopleQuerySchema>;

/**
 * ET MENNESKE SOM IKKE STÅR I REGISTERET ENNÅ (QA K6).
 *
 * Navnet alene holder; e-post og telefon er det man har for hånden. Ingen firma, ingen
 * kontakttype, ingen av de tjue feltene `CreateContactRequest` kan sette — dette er «legg en
 * gitarist i reisefølget», ikke «før en ny kontakt i registeret».
 */
export const newPerformanceContactSchema = z
  .object({
    displayName: z.string().trim().min(1).max(120),
    email: z.email().max(320).nullish(),
    phone: z.string().trim().min(1).max(40).nullish(),
    /**
     * DET SAMME MENNESKET, IKKE ET NYTT (sak 216). Byrået som setter sitt eget bandmedlem i
     * reisefølget sender personen kontakten deres peker på. Basen godtar den bare når personen alt
     * er en kontakt hos et av husene som er part i programposten (0376).
     */
    personId: z.uuid().nullish(),
  })
  .meta({ id: 'NewPerformanceContact' });
export type NewPerformanceContact = z.infer<typeof newPerformanceContactSchema>;

export const addPerformancePersonRequestSchema = z
  .object({
    performanceId: z.uuid(),
    /**
     * EN KONTAKT SOM FINNES, ELLER ET MENNESKE SOM SKAL BLI DET — nøyaktig én av dem (QA K6).
     *
     * Skjemaet gjorde to kall: opprett kontakten, koble den på. Feilet det andre, ble det første
     * stående — og rapporten fant nøyaktig det, som to «QA Gitarist» i byråets register. Med begge
     * i ÉN forespørsel er det basen som holder dem sammen, og «enten begge eller ingen» er noe
     * transaksjonen garanterer framfor noe koden prøver på.
     */
    contactId: z.uuid().optional(),
    newContact: newPerformanceContactSchema.optional(),
    party: performancePartySchema,
    role: z.string().trim().min(1).max(MAX_PERFORMANCE_ROLE).nullish(),
    accreditationLevelId: z.uuid().nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => (body.contactId == null) !== (body.newContact == null), {
    message: 'Give either an existing contact or a new one, never both.',
    path: ['contactId'],
  })
  .meta({ id: 'AddPerformancePersonRequest' });
export type AddPerformancePersonRequest = z.infer<typeof addPerformancePersonRequestSchema>;

/**
 * `contactId` never moves. Putting a different person in a row would carry their predecessor's
 * badge, their room and their allergies with it — the row is about a person, so a different person
 * is a different row.
 *
 * `party` DOES move: an agent who decides to travel with the band after all is the same person on
 * the same act, and re-typing them would lose the role and the badge somebody already set.
 */
export const updatePerformancePersonRequestSchema = z
  .object({
    party: performancePartySchema.optional(),
    role: z.string().trim().min(1).max(MAX_PERFORMANCE_ROLE).nullish(),
    accreditationLevelId: z.uuid().nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    allergy: z.string().trim().min(1).max(MAX_DIETARY_TEXT).nullish(),
    diet: z.string().trim().min(1).max(MAX_DIETARY_TEXT).nullish(),
    dietaryNotes: z.string().trim().min(1).max(MAX_DIETARY_TEXT).nullish(),
  })
  .meta({ id: 'UpdatePerformancePersonRequest' });
export type UpdatePerformancePersonRequest = z.infer<typeof updatePerformancePersonRequestSchema>;

/**
 * Akkreditering til hele gruppen på én gang.
 *
 * ---------------------------------------------------------------------------------------------
 * «AKKREDITERING DELER UT TIL ALLE, IKKE KUN 1 ELLER FLERE» (Atle, 1. september).
 *
 * Setting the same badge on nine people one dialog at a time is nine dialogs to say one thing, and
 * the thing it says is the ordinary case: a touring party gets ONE level, and the exceptions are
 * exceptions. So the group is the unit, and the individual row stays editable for the tour manager
 * who genuinely needs All Access while the rest have Backstage.
 *
 * `party` is nullish because both questions are real: «alle i reisefølget» is the common one, and
 * «alle på programposten» is what you want the evening you accredit everybody at once.
 *
 * `overwrite` is required rather than defaulted, and false is the safe answer: somebody who has
 * already been given a level was given it BY somebody, and quietly replacing that is the kind of
 * bulk action people stop trusting. The client says which it means; nothing here guesses.
 */
export const setGroupAccreditationRequestSchema = z
  .object({
    performanceId: z.uuid(),
    /** Absent or null means everybody on the act, both groups. */
    party: performancePartySchema.nullish(),
    accreditationLevelId: z.uuid(),
    overwrite: z.boolean(),
  })
  .meta({ id: 'SetGroupAccreditationRequest' });
export type SetGroupAccreditationRequest = z.infer<typeof setGroupAccreditationRequestSchema>;

/**
 * «ANGRE» PÅ «FJERN FRA PROGRAMPOSTEN» (F23, Atle 04.10). Raden ble myk-slettet, og angringen tar
 * den tilbake. Programposten står i kroppen fordi ressursoppslaget går gjennom den — en slettet rad
 * finnes ikke for resolveren.
 */
export const restorePerformancePersonRequestSchema = z
  .object({ id: z.uuid(), performanceId: z.uuid() })
  .meta({ id: 'RestorePerformancePersonRequest' });
export type RestorePerformancePersonRequest = z.infer<typeof restorePerformancePersonRequestSchema>;
